import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API_URL, getAuthHeaders } from "../../lib/utils";
import { useAuth } from "../../context/AuthContext";
import { visitorApi } from "./visitorUtils";

const STORAGE_KEY = "visitors.scope";

const readStored = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

/**
 * Compañía e instalación seleccionadas en el módulo de visitas.
 * Se recuerdan entre pantallas para que el vigilante no tenga que elegir su puesto cada vez.
 */
export const useVisitorScope = () => {
  const { user, canManage, canManageCompanies } = useAuth();
  const stored = readStored();
  const [companies, setCompanies] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [companyId, setCompanyIdState] = useState(
    canManageCompanies() ? stored.companyId || "all" : user?.company_id
  );
  const [facilityId, setFacilityIdState] = useState(stored.facilityId || "all");
  const [loaded, setLoaded] = useState(false);

  const persist = (next) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readStored(), ...next }));
    } catch {
      // Almacenamiento no disponible: la selección solo dura la sesión
    }
  };

  const setCompanyId = (value) => {
    setCompanyIdState(value);
    setFacilityIdState("all");
    persist({ companyId: value, facilityId: "all" });
  };

  const setFacilityId = (value) => {
    setFacilityIdState(value);
    persist({ facilityId: value });
  };

  const loadFacilities = useCallback(async () => {
    try {
      const data = await visitorApi.get("/facilities", { company_id: companyId });
      setFacilities(data);
      if (facilityId !== "all" && !data.some((f) => f.id === facilityId)) {
        setFacilityIdState("all");
      }
    } catch (e) {
      console.error("Error cargando instalaciones:", e);
    } finally {
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (canManageCompanies()) {
      axios
        .get(`${API_URL}/companies`, { headers: getAuthHeaders() })
        .then((r) => setCompanies(r.data))
        .catch((e) => console.error("Error cargando compañías:", e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadFacilities();
  }, [loadFacilities]);

  const facility = facilities.find((f) => f.id === facilityId) || null;

  return {
    user,
    isManager: canManage(),
    canSelectCompany: canManageCompanies(),
    companies,
    companyId,
    setCompanyId,
    facilities,
    facilityId,
    setFacilityId,
    facility,
    reloadFacilities: loadFacilities,
    loaded,
    // Parámetros listos para enviar a la API
    params: { company_id: companyId, facility_id: facilityId },
    // Compañía efectiva para crear registros
    effectiveCompanyId: companyId && companyId !== "all" ? companyId : facility?.company_id || user?.company_id,
  };
};
